import Vue from "vue";
import { ActionTree, GetterTree, MutationTree, Module } from "vuex";
import { RootState } from "@/store/states/RootState";
import { CartState, CartItem } from "@/store/states/CartState";
import { ShopItemOptionChoiceDefinition } from "@/store/states/ShopState";
import { v4 as uuid } from "uuid";
import shopItemRawDefinitions from "@/assets/shop-items-definitions.json";

import axios from "axios";

const state: CartState = {
  uuid: undefined,
  current: undefined,
  transactionCount: 0,
  contact: {
    title: "",
    firstname: "",
    lastname: "",
    companyname: "",
    email: "",
    address: "",
    npa: "",
    city: "",
    country: "",
    delivery: {
      title: "",
      firstname: "",
      lastname: "",
      companyname: "",
      address: "",
      npa: "",
      city: "",
      country: "",
    },
  },
};

// getters
const getters: GetterTree<CartState, RootState> = {
  /**
   * Returns the quantity if it exists,
   * returns undefined otherwise
   */
  quantity(state) {
    if (state.current) {
      let result = 0;
      shopItemRawDefinitions.options.forEach((option) => {
        if (option.type === "quantity") {
          if (state.current.itemOptions[option.uuid]) {
            result = state.current.itemOptions[option.uuid]["quantity"];
          }
        }
      });
      return result;
    }
    return undefined;
  },

  /**
   * Returns the price,
   */
  price(state) {
    if (state.current) {
        let result = 0.0;
        shopItemRawDefinitions.options.forEach((option) => {
            if (option.uuid === "0110070b-930c-4368-b650-11177c8176f7") {
                const selectedUUID = Object.keys(state.current.itemOptions[option.uuid])[0]
                const selectedTypeDefinition: ShopItemOptionChoiceDefinition = option.options.find(
                    (x) => x.uuid === selectedUUID
                );
                result = selectedTypeDefinition['price'];
            }
        });
        return result
    }
    return undefined;
  }
};

// actions
const actions: ActionTree<CartState, RootState> = {
  /**
   * Init the store if it's not yet initialized
   */
  init({ commit }) {
    commit("INIT");
  },

  /**
   * Reset the store
   */
  reset({ commit }) {
    localStorage.removeItem("shopItemUuid");
    return commit("RESET");
  },

  /**
   * Save contact
   */
  saveContact({ commit }, contact) {
    commit("SAVE_CONTACT", contact);
  },

  /**
   * Load the current shop item from the backend if it exists, create a new one otherwise
   */
  loadOrCreateShopItem({ commit, dispatch }, itemDefinitionUuid) {
    return new Promise((resolve) => {
      if (localStorage.shopItemUuid) {
        axios
          .get(
            process.env.VUE_APP_BACKEND_URL +
              "/shopitem/" +
              localStorage.shopItemUuid
          )
          .then((response) => {
            commit("SELECT_NEW_PRODUCT", response.data);
            resolve(response);
          })
          .catch(() => {
            dispatch("reset").then(() => {
              dispatch("createShopItem", itemDefinitionUuid);
            });
            resolve();
          });
      } else {
        dispatch("createShopItem", itemDefinitionUuid);
        resolve();
      }
    });
  },

  /**
   * Create a new shopitem
   */
  createShopItem({ commit }, itemDefinitionUuid) {
    const itemUuid = uuid();

    const item: CartItem = {
      uuid: itemUuid,
      itemDefinitionUuid: itemDefinitionUuid,
      itemOptions: {},
    };

    axios.post(process.env.VUE_APP_BACKEND_URL + "/shopitem", item).then(() => {
      commit("SELECT_NEW_PRODUCT", item);
      localStorage.shopItemUuid = itemUuid;
    });
  },

  /**
   * Select from a multiple choice option
   */
  selectOptionFromMultipleChoice({ commit }, { itemOptionUuid, choiceUuid }) {
    commit("SELECT_OPTION", { itemOptionUuid, choiceUuid });
    axios
      .post(
        process.env.VUE_APP_BACKEND_URL + "/shopitem",
        this.state.cart.current
      )
      .catch(() => {
        // Rollback mutation in case we can't save the new value
        commit("ROLLBACK_OPTION", itemOptionUuid);
      });
  },

  /**
   * Set a single option with a given value
   */
  setOption({ commit }, { itemOptionUuid, optionKey, optionValue }) {
    commit("SELECT_OPTION_WITH_VALUE", {
      itemOptionUuid,
      optionKey,
      optionValue,
    });
    axios
      .post(
        process.env.VUE_APP_BACKEND_URL + "/shopitem",
        this.state.cart.current
      )
      .catch(() => {
        // Rollback mutation in case we can't save the new value
        commit("ROLLBACK_OPTION_WITH_VALUE", { itemOptionUuid, optionKey });
      });
  },
};

/// mutations
const mutations: MutationTree<CartState> = {
  INIT(state) {
    if (state.uuid === undefined) {
      state.uuid = uuid();
    } else {
      console.log(
        "No need to initialize cart, already done in a previous session."
      );
    }
  },

  RESET(state) {
    (state.current = undefined),
      (state.uuid = undefined),
      (state.uuid = uuid());
  },

  SELECT_NEW_PRODUCT(state, item) {
    state.current = item;
    state.transactionCount += 1;
  },

  SELECT_OPTION(state, { itemOptionUuid, choiceUuid }) {
    state.transactionCount += 1;
    Vue.set(state.current.itemOptions, itemOptionUuid, {});
    Vue.set(state.current.itemOptions[itemOptionUuid], choiceUuid, true);
  },

  ROLLBACK_OPTION(state, itemOptionUuid) {
    Vue.set(state.current.itemOptions, itemOptionUuid, {});
  },

  ROLLBACK_OPTION_WITH_VALUE(state, { itemOptionUuid, optionKey }) {
    state.current.itemOptions[itemOptionUuid][optionKey] = undefined;
    Vue.set(state.current.itemOptions[itemOptionUuid], optionKey, {});
  },

  SELECT_OPTION_WITH_VALUE(state, { itemOptionUuid, optionKey, optionValue }) {
    state.transactionCount += 1;
    if (state.current.itemOptions[itemOptionUuid] === undefined) {
      Vue.set(state.current.itemOptions, itemOptionUuid, {});
    }
    Vue.set(state.current.itemOptions[itemOptionUuid], optionKey, optionValue);
  },

  SAVE_CONTACT(state, contact) {
    state.contact = contact;
  },
};

export const cart: Module<CartState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
